import { Button, Modal } from 'antd';
import { Input } from '../../../../components';

import { useState, type FC } from 'react';
import clsx from 'clsx';
import { type IQuestionAndAnswer, type SetAnswer } from '../../TestPage';
import {
  IconInfoCircle,
  IconStar,
  IconStarFilled,
  IconCheck,
  IconX,
  IconFlag,
  IconBookmark,
  IconEyeOff
} from '@tabler/icons-react';
import { useSaveQuestion } from '../../../../services/userQuestion';
import { useModal } from '../../../../hooks/useModal';
import { useCreateRatedQuestion } from '../../../../services/ratedQuestion';
import { useCreateImpugnatedQuestion } from '../../../../services/impugnatedQuestion';
import { useHideQuestion } from '../../../../services/user';

interface Props {
  failed?: boolean
  index: number
  onChange: (val: SetAnswer) => void
  question: IQuestionAndAnswer
  showAnswer?: boolean
  showCorrect?: boolean
  userTest?: string
}

const Question: FC<Props> = ({ failed, index, onChange, question, showAnswer, showCorrect, userTest }) => {
  const [rated, setRated] = useState(false);
  const { saveQuestion, loading } = useSaveQuestion();
  const { createRatedQuestion, loading: loadingRate } = useCreateRatedQuestion();
  const { createImpugnation, loading: impugnating } = useCreateImpugnatedQuestion();
  const { hideQuestion, loading: hidding } = useHideQuestion();

  const [hidden, setHidden] = useState(false);
  const [saved, setSaved] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);
  const [comment, setComment] = useState('');
  const [impugReason, setImpugReason] = useState('');
  const [impugDone, setImpugDone] = useState(false);

  const { visible, toggleModal } = useModal();
  const { visible: modalComment, toggleModal: toggleModalComment } = useModal();
  const { visible: modalImpugnate, toggleModal: toggleModalImpugnate } = useModal();

  const handleChange = (checked: boolean, answer: string) => {
    if (checked) {
      onChange({
        question: question._id!,
        value: answer
      });
    } else {
      onChange({
        question: question._id!,
        value: undefined
      });
    }
  };

  const handleSaveQuestion = async () => {
    if (userTest) {
      const savedQuestion = await saveQuestion({
        question: question._id!,
        userTest,
        comment: comment || '-'
      });

      if (savedQuestion?._id) {
        toggleModalComment();
        setSaved(true);
      }
    }
  };

  const handleRateQuestion = async () => {
    await createRatedQuestion({
      question: question._id!,
      rating: selectedRating
    });

    setRated(true);
    toggleModal();
  };

  const handleImpugQuestion = async () => {
    await createImpugnation({
      reason: impugReason,
      question: question._id!
    });
    toggleModalImpugnate();
    setImpugDone(true);
  };

  const isAnswerCorrect = (answer: string) => answer === question.correctAnswer;
  const isUserAnswer = (answer: string) => question.userAnswer === answer;
  const isCorrect = showAnswer
    ? question.userAnswer === question.correctAnswer
    : failed !== undefined
      ? !failed
      : false;
  const isWrong = showAnswer
    ? question.userAnswer !== question.correctAnswer
    : failed !== undefined
      ? failed
      : false;

  const handleHideQuestion = async () => {
    await hideQuestion(question._id!);

    setHidden(true);
  };

  const getShowCorrect = (answer: string) => {
    if (showCorrect) return answer === question.correctAnswer;
    return question.userAnswer === answer;
  };

  return (
    <div className={clsx(
      'relative mb-6 transition-all duration-300',
      'rounded-xl border overflow-hidden',
      'bg-white border-gray-200 shadow-md',
      'dark:bg-surface-dark dark:border-borders dark:shadow-lg',
      // Status indicator bar on the left
      (isCorrect || isWrong) && 'border-l-4',
      isCorrect && 'border-l-green-500 dark:border-l-green-400',
      isWrong && 'border-l-red-500 dark:border-l-red-400'
    )}>
      {/* Status Badge */}
      {(showAnswer || failed !== undefined) && (
        <div className={clsx(
          'absolute top-4 right-4 z-10 px-3 py-1.5 rounded-full text-xs font-semibold',
          'flex items-center gap-1.5 shadow-md',
          isCorrect
            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300'
            : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
        )}>
          {isCorrect ? (
            <>
              <IconCheck size={14} />
              <span>Correcta</span>
            </>
          ) : (
            <>
              <IconX size={14} />
              <span>Incorrecta</span>
            </>
          )}
        </div>
      )}

      <div className="p-6">
        {/* Question Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className={clsx(
            'flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg',
            'bg-gradient-to-br from-blue-500 to-purple-500 text-white',
            'dark:from-blue-600 dark:to-purple-600'
          )}>
            {index}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={clsx(
              'text-lg font-semibold leading-relaxed transition-colors duration-300',
              'text-gray-900 dark:text-white'
            )}>
              {question.title}
            </h3>
          </div>
          {question.image && (
            <div className="flex-shrink-0">
              <img
                src={question.image}
                alt="Question illustration"
                className={clsx(
                  'max-w-[150px] md:max-w-[200px] rounded-lg border',
                  'border-gray-200 dark:border-borders',
                  'shadow-sm'
                )}
              />
            </div>
          )}
        </div>

        {/* Answers */}
        <div className="space-y-3 mb-6">
          {question.answers.map((answer, answerIndex) => {
            const isCorrectAnswer = isAnswerCorrect(answer);
            const isSelected = getShowCorrect(answer);
            const userSelected = isUserAnswer(answer);

            // When showing results
            const showAsCorrect = showAnswer && isCorrectAnswer;
            const showAsUserWrong = showAnswer && userSelected && !isCorrectAnswer;
            // When not showing results, just show selected state
            const showAsSelected = !showAnswer && isSelected;

            return (
              <div
                key={answer}
                className={clsx(
                  'relative p-4 rounded-lg border-2 transition-all duration-300',
                  'cursor-pointer',
                  // Base styles
                  'bg-gray-50 border-gray-200',
                  'dark:bg-surface-medium dark:border-borders',
                  // Hover (only when not showing answer)
                  !showAnswer && !failed && 'hover:border-blue-400 hover:bg-blue-50 dark:hover:border-blue-500 dark:hover:bg-blue-900/20',
                  // Selected state (when taking test)
                  showAsSelected && 'border-blue-500 bg-blue-50 dark:border-blue-500 dark:bg-blue-900/30',
                  // Correct answer (when showing results)
                  showAsCorrect && 'border-green-500 bg-green-50 dark:border-green-500 dark:bg-green-900/30',
                  // User's wrong answer (when showing results)
                  showAsUserWrong && 'border-red-500 bg-red-50 dark:border-red-500 dark:bg-red-900/30'
                )}
                onClick={() => {
                  if (!showAnswer && failed === undefined) {
                    handleChange(!isSelected, answer);
                  }
                }}
              >
                <div className="flex items-start gap-3">
                  <div className={clsx(
                    'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                    'border-2 transition-colors duration-300',
                    showAsCorrect
                      ? 'border-green-500 bg-green-500 text-white'
                      : showAsUserWrong
                        ? 'border-red-500 bg-red-500 text-white'
                        : showAsSelected
                          ? 'border-blue-500 bg-blue-500 text-white'
                          : 'border-gray-300 bg-white text-gray-400 dark:border-gray-600 dark:bg-surface-dark dark:text-gray-500'
                  )}>
                    {answerIndex + 1}
                  </div>
                  <p className={clsx(
                    'flex-1 text-base transition-colors duration-300',
                    showAsCorrect
                      ? 'text-green-700 font-semibold dark:text-green-300'
                      : showAsUserWrong
                        ? 'text-red-700 font-semibold dark:text-red-300'
                        : 'text-gray-700 dark:text-gray-300'
                  )}>
                    {answer}
                  </p>
                  {showAsCorrect && (
                    <IconCheck
                      size={20}
                      className="flex-shrink-0 text-green-600 dark:text-green-400"
                    />
                  )}
                  {showAsUserWrong && (
                    <IconX
                      size={20}
                      className="flex-shrink-0 text-red-600 dark:text-red-400"
                    />
                  )}
                  {showAnswer && userSelected && !isCorrectAnswer && (
                    <span className={clsx(
                      'flex-shrink-0 px-2 py-0.5 rounded text-xs font-semibold',
                      'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                    )}>
                      Tu respuesta
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Info Box */}
        {question.info && showAnswer && (
          <div className={clsx(
            'mb-6 p-4 rounded-lg flex items-start gap-3',
            'bg-blue-50 border border-blue-200',
            'dark:bg-blue-900/30 dark:border-blue-800'
          )}>
            <IconInfoCircle
              size={20}
              className={clsx(
                'flex-shrink-0 mt-0.5',
                'text-blue-600 dark:text-blue-400'
              )}
            />
            <p className={clsx(
              'text-sm leading-relaxed',
              'text-blue-800 dark:text-blue-200'
            )}>
              {question.info}
            </p>
          </div>
        )}

        {/* Actions */}
        {showAnswer && (
          <div className={clsx(
            'pt-6 border-t',
            'border-gray-200 dark:border-borders'
          )}>
            <div className="flex flex-wrap gap-3 justify-end">
              <Button
                disabled={impugDone}
                loading={impugnating}
                onClick={toggleModalImpugnate}
                className={clsx(
                  'flex items-center gap-2',
                  'border-red-300 text-red-600',
                  'dark:border-red-700 dark:text-red-400',
                  'hover:border-red-400 hover:text-red-700',
                  'dark:hover:border-red-600 dark:hover:text-red-300'
                )}
                icon={<IconFlag size={16} />}
              >
                {impugDone ? 'Impugnada' : 'Impugnar pregunta'}
              </Button>
              <Button
                loading={loadingRate}
                onClick={toggleModal}
                disabled={rated}
                className={clsx(
                  'flex items-center gap-2',
                  rated
                    ? 'bg-green-100 text-green-700 border-green-300 dark:bg-green-900/40 dark:text-green-300 dark:border-green-800'
                    : 'border-amber-300 text-amber-600 dark:border-amber-700 dark:text-amber-400 hover:border-amber-400 hover:text-amber-700 dark:hover:border-amber-600 dark:hover:text-amber-300'
                )}
                icon={<IconStar size={16} />}
              >
                {rated ? 'Valorada' : 'Valorar dificultad'}
              </Button>
              <Button
                disabled={saved}
                onClick={toggleModalComment}
                loading={loading}
                className={clsx(
                  'flex items-center gap-2',
                  saved
                    ? 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-900/40 dark:text-blue-300 dark:border-blue-800'
                    : 'border-blue-300 text-blue-600 dark:border-blue-700 dark:text-blue-400 hover:border-blue-400 hover:text-blue-700 dark:hover:border-blue-600 dark:hover:text-blue-300'
                )}
                icon={<IconBookmark size={16} />}
              >
                {saved ? 'Pregunta guardada' : 'Guardar pregunta'}
              </Button>
              <Button
                disabled={hidden}
                onClick={handleHideQuestion}
                loading={hidding}
                className={clsx(
                  'flex items-center gap-2',
                  hidden
                    ? 'bg-gray-100 text-gray-700 border-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700'
                    : 'border-gray-300 text-gray-600 dark:border-gray-700 dark:text-gray-400 hover:border-gray-400 hover:text-gray-700 dark:hover:border-gray-600 dark:hover:text-gray-300'
                )}
                icon={<IconEyeOff size={16} />}
              >
                {hidden ? 'Pregunta oculta' : 'Ocultar pregunta'}
              </Button>
            </div>
          </div>
        )}
      </div>
      <Modal
        open={modalImpugnate}
        onCancel={toggleModalImpugnate}
        onOk={handleImpugQuestion}
        okButtonProps={{ loading: impugnating, disabled: !impugReason }}
        okText="Enviar"
        title="Impugnar pregunta"
      >
        <p style={{ marginBottom: 20 }}>Escribe el motivo por el cual crees que esta pregunta debería ser impugnada para que nuestros profesores puedas valorarlo. Muchas gracias por tu ayuda!</p>
        <Input placeholder='Comentario' value={impugReason} onChange={(e) => setImpugReason(e.target.value)} />
      </Modal>
      <Modal
        open={modalComment}
        onCancel={toggleModalComment}
        okButtonProps={{ loading }}
        onOk={handleSaveQuestion}
        okText="Guardar"
        title="Añadir comentario"
      >
        <p style={{ marginBottom: 20 }}>Añade un comentario con información que pueda serte útil de cara al futuro (no es obligatorio)</p>
        <Input placeholder='Comentario' value={comment} onChange={(e) => setComment(e.target.value)} />
      </Modal>
      <Modal
        open={visible}
        onCancel={toggleModal}
        okButtonProps={{
          loading: loadingRate
        }}
        onOk={handleRateQuestion}
        okText="Guardar valoración"
        title="Valorar pregunta"
      >
        <p className={clsx(
          'mb-6 text-base transition-colors duration-300',
          'text-gray-700 dark:text-gray-300'
        )}>
          Valora del 1 al 5 la pregunta teniendo en cuenta su dificultad (1 más fácil, 5 más difícil)
        </p>
        <div className={clsx(
          'flex items-center justify-center gap-2 py-4'
        )}>
          {[1, 2, 3, 4, 5].map((rating) => (
            <button
              key={rating}
              onClick={() => setSelectedRating(rating)}
              className={clsx(
                'p-2 rounded-lg transition-all duration-300',
                'hover:scale-110 hover:bg-gray-100 dark:hover:bg-surface-medium',
                'focus:outline-none focus:ring-2 focus:ring-offset-2',
                'focus:ring-blue-500 dark:focus:ring-blue-400'
              )}
              type="button"
            >
              {rating <= selectedRating ? (
                <IconStarFilled
                  size={32}
                  className={clsx(
                    'text-amber-500 dark:text-amber-400',
                    'transition-colors duration-300'
                  )}
                />
              ) : (
                <IconStar
                  size={32}
                  className={clsx(
                    'text-gray-300 dark:text-gray-600',
                    'transition-colors duration-300'
                  )}
                />
              )}
            </button>
          ))}
        </div>
      </Modal>
    </div>
  );
};

export { Question };
